import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    Box,
    TextField,
    Typography,
    Autocomplete,
    Checkbox
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Venue } from "../../../helper";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_VENUES_WITH_NAME_FILTER } from "../../AddTickets/api/addTicketsQuery";
import { getAutocompleteStyles } from "../../../style/getAutoCompleteStyles";
import { useDebounce } from "../../../hooks/useDebounce";

const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

interface CreateVenueModalProps {
    open: boolean;
    onClose: () => void;
    onAdd: (venues: Venue[]) => void;
}

export default function CreateVenueModal({
    open,
    onClose,
    onAdd,
}: CreateVenueModalProps) {
    const [inputValue, setInputValue] = useState('');
    const debouncedValue = useDebounce(inputValue, 500);

    const [selectedVenues, setSelectedVenues] = useState<Venue[]>([]);
    const [listOfVenue, setlistOfVenue] = useState<Venue[]>([]);
    const [getlistOfVenue, { loading }] = useLazyQuery(GET_ALL_VENUES_WITH_NAME_FILTER, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setlistOfVenue(data?.all_venues ?? []);
        },
        onError: (err) => {
            console.log("Error at GetlistOfVenue", err?.message)
        }
    });

    const handleAdd = () => {
        if (!selectedVenues.length) return;
        onAdd(selectedVenues);
        onClose();
        setSelectedVenues([]);
    };

    const handleInput = (_event: any, value: string) => {
        setInputValue(value);
    };

    useEffect(() => {
        if (debouncedValue.length >= 3) {
            getlistOfVenue({
                variables: { name: `${debouncedValue}%` },
            });
        }
    }, [debouncedValue]);

    useEffect(() => {
        setSelectedVenues([]);
        setInputValue('');
    }, [open]);

    useEffect(() => {
        if (!open) {
            setSelectedVenues([]);
            setInputValue('');
            setlistOfVenue([]);
        }
    }, [open]);

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h6" mb={2}>
                    Add Venues
                </Typography>
                <div>
                    <Autocomplete
                        multiple
                        disablePortal
                        disableCloseOnSelect
                        id="venues-multi-select"
                        sx={getAutocompleteStyles(true)}
                        options={listOfVenue ?? []}
                        loading={loading}
                        value={selectedVenues}
                        onChange={(_event, value) => setSelectedVenues(value)}
                        onInputChange={handleInput}
                        autoHighlight
                        getOptionLabel={(option: Venue) =>
                            [
                                [
                                    option.av_name,
                                    option.av_street,
                                    option.av_city,
                                    option.av_state_code,
                                    option.av_country_code,
                                    option.av_zipcode,
                                ]
                                    .filter(Boolean)
                                    .join(', '),
                                option.av_venue_ref_id,
                            ].join(" - ")
                        }
                        isOptionEqualToValue={(option: Venue, value: Venue) =>
                            option.av_id === value.av_id
                        }
                        getOptionKey={(option: Venue) => option.av_id}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {[
                                    [
                                        option.av_name,
                                        option.av_street,
                                        option.av_city,
                                        option.av_state_code,
                                        option.av_country_code,
                                        option.av_zipcode,
                                    ]
                                        .filter(Boolean)
                                        .join(', '),
                                    option.av_venue_ref_id,
                                ].join(" - ")}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select Venues"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                        noOptionsText={
                            debouncedValue.length < 3
                                ? "Type at least 3 characters"
                                : "No venues found"
                        }
                    />
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleAdd}
                    disabled={!selectedVenues.length}
                >
                    Add Venues
                </Button>
            </Box>
        </Modal>
    );
}
